/**
 * คลังเอกสารสัญญา — เห็นรายชื่อเอกสารทั้งหมดแบบ Google Docs · กดเปิดเพื่อแก้ในหน้ากระดาษ
 * (ContractTemplateEditor.tsx) · ทำสำเนา / ตั้งเป็นค่าเริ่มต้น / สร้างเอกสารเปล่า
 * (ยกจาก sugaray — ฉบับค่าเริ่มต้นของสุกรีคือสัญญามาตรฐานเดิม SK-STANDARD)
 * แบ่ง 2 แท็บ: สัญญาจ้าง (เลือกใช้ตอนออกสัญญา) · ข้อตกลงพิเศษ (ข้อความแนบท้ายสัญญา)
 * เฉพาะเจ้าของและฝ่ายบุคคลกลาง
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FileText, Plus, Copy, Star, Search, FilePlus2 } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { EmptyState } from '../../components/ui/EmptyState';
import { useToast } from '../../components/ui/toast';
import {
  useContractTemplates, useCreateContractTemplate, useDuplicateContractTemplate,
  useSetDefaultContractTemplate, type ContractTemplateCategory, type ContractTemplateSummary,
} from '../../hooks';

export const CONTRACT_KIND_LABEL: Record<string, string> = {
  probation: 'ทดลองงาน', fixed_term: 'มีกำหนดระยะเวลา', permanent: 'ไม่มีกำหนด', daily: 'รายวัน',
};

const TABS: Array<{ key: ContractTemplateCategory; label: string; hint: string }> = [
  { key: 'contract', label: 'สัญญาจ้าง', hint: 'เลือกใช้ตอนสร้างสัญญาให้พนักงาน (หน้าเอกสารพนักงาน) — ฉบับที่ติดดาวคือฉบับที่ระบบเลือกให้ก่อน' },
  { key: 'addendum', label: 'ข้อตกลงพิเศษ', hint: 'ข้อความแนบท้ายสัญญาสำหรับบางตำแหน่ง เช่น ห้ามค้าแข่ง รักษาความลับ ค่าคอมมิชชั่น — ใส่เข้าช่องเงื่อนไขเพิ่มเติมตอนสร้างสัญญาได้' },
];

const TH_MONTHS_SHORT = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];

/** timestamptz → วันที่ตามเวลาไทย ("19 ก.ย. 69") */
export function thaiUpdatedDate(ts: string): string {
  try {
    const [y, m, d] = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Bangkok' }).format(new Date(ts)).split('-').map(Number);
    return `${d} ${TH_MONTHS_SHORT[(m ?? 1) - 1]} ${((y ?? 0) + 543) % 100}`;
  } catch {
    return ts.slice(0, 10);
  }
}

export function ContractTemplate() {
  const [params, setParams] = useSearchParams();
  const tab: ContractTemplateCategory = params.get('tab') === 'addendum' ? 'addendum' : 'contract';
  const { data, isLoading, error } = useContractTemplates();
  const create = useCreateContractTemplate();
  const toast = useToast();
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  const all = data?.templates ?? [];
  const counts = { contract: 0, addendum: 0 };
  for (const t of all) counts[t.category] += 1;

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all
      .filter((t) => t.category === tab)
      .filter((t) => !needle || t.name.toLowerCase().includes(needle) || t.code.toLowerCase().includes(needle))
      .sort((a, b) => Number(b.is_active) - Number(a.is_active) || a.sort - b.sort || a.code.localeCompare(b.code));
  }, [all, tab, q]);

  async function onCreateBlank() {
    try {
      const r = await create.mutateAsync({
        name: tab === 'contract' ? 'สัญญาจ้างฉบับใหม่' : 'ข้อตกลงพิเศษฉบับใหม่',
        category: tab,
      });
      navigate(`/owner/contract-template/${r.template.id}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'สร้างเอกสารไม่สำเร็จ');
    }
  }

  const current = TABS.find((t) => t.key === tab)!;

  return (
    <AdminLayout
      title="แก้ถ้อยคำสัญญา"
      subtitle="คลังเอกสารสัญญา — กดเปิดเอกสารเพื่อแก้ข้อความ แล้วเลือกใช้ตอนสร้างสัญญาได้ทันที"
      actions={(
        <button className="btn-primary" disabled={create.isPending} onClick={onCreateBlank}>
          <Plus className="w-4 h-4" />{create.isPending ? 'กำลังสร้าง…' : 'สร้างเอกสารเปล่า'}
        </button>
      )}
    >
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setParams(t.key === 'contract' ? {} : { tab: t.key }, { replace: true })}
              className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm ${tab === t.key ? 'bg-brand-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              {t.label} <span className="opacity-75">({counts[t.key]})</span>
            </button>
          ))}
        </div>
        <div className="relative sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-3 w-4 h-4 text-gray-400" />
          <input className="input pl-9" placeholder="ค้นหาชื่อเอกสาร" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>
      <p className="mb-3 text-sm text-gray-500">{current.hint}</p>

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="card h-44 animate-pulse bg-gray-100" />)}
        </div>
      ) : error ? (
        <EmptyState icon={FileText} title="โหลดคลังเอกสารไม่สำเร็จ" detail={error instanceof Error ? error.message : undefined} />
      ) : shown.length === 0 ? (
        <EmptyState
          icon={FilePlus2}
          title={q ? 'ไม่พบเอกสารที่ค้นหา' : 'ยังไม่มีเอกสารในแท็บนี้'}
          detail={q ? 'ลองค้นด้วยคำอื่น' : 'กด "สร้างเอกสารเปล่า" ด้านบนเพื่อเริ่มเขียนฉบับแรก'}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {shown.map((t) => <TemplateCard key={t.id} t={t} />)}
        </div>
      )}
    </AdminLayout>
  );
}

function TemplateCard({ t }: { t: ContractTemplateSummary }) {
  const duplicate = useDuplicateContractTemplate();
  const setDefault = useSetDefaultContractTemplate();
  const toast = useToast();
  const navigate = useNavigate();

  async function onDuplicate() {
    try {
      const r = await duplicate.mutateAsync(t.id);
      toast.success(`ทำสำเนาแล้ว — เปิด "${r.template.name}" ให้แก้ต่อ`);
      navigate(`/owner/contract-template/${r.template.id}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ทำสำเนาไม่สำเร็จ');
    }
  }

  async function onSetDefault() {
    try {
      await setDefault.mutateAsync(t.id);
      toast.success(`ตั้ง "${t.name}" เป็นฉบับค่าเริ่มต้นแล้ว`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ตั้งค่าเริ่มต้นไม่สำเร็จ');
    }
  }

  return (
    <div className={`card flex min-w-0 flex-col overflow-hidden ${t.is_active ? '' : 'opacity-60'}`}>
      <Link to={`/owner/contract-template/${t.id}`} className="group block min-w-0 flex-1">
        {/* ภาพย่อหน้ากระดาษ */}
        <div className="flex h-20 items-end justify-center border-b border-gray-100 bg-gray-50 pt-3">
          <div className="h-full w-24 rounded-t border border-b-0 border-gray-200 bg-white px-2 pt-2 shadow-sm transition group-hover:border-brand-300">
            {[80, 95, 70, 90, 60].map((w, i) => (
              <div key={i} className="mb-1 h-1 rounded bg-gray-200" style={{ width: `${w}%` }} />
            ))}
          </div>
        </div>
        <div className="min-w-0 p-3">
          <div className="flex items-start gap-1.5">
            {t.is_default && <Star className="mt-0.5 w-4 h-4 shrink-0 fill-amber-400 text-amber-400" aria-label="ฉบับค่าเริ่มต้น" />}
            <div className="min-w-0 font-medium leading-snug text-gray-900 group-hover:text-brand-700">{t.name}</div>
          </div>
          <div className="mt-0.5 truncate text-xs text-gray-400">{t.code} · แก้ล่าสุด {thaiUpdatedDate(t.updated_at)}</div>
          <div className="mt-2 flex flex-wrap gap-1">
            {t.is_default && <span className="whitespace-nowrap rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-800">ค่าเริ่มต้น</span>}
            {!t.is_active && <span className="whitespace-nowrap rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">ปิดใช้</span>}
            {t.suggested_kinds.map((k) => (
              <span key={k} className="whitespace-nowrap rounded bg-sky-50 px-1.5 py-0.5 text-xs text-sky-800">
                เหมาะกับ{CONTRACT_KIND_LABEL[k] ?? k}
              </span>
            ))}
          </div>
        </div>
      </Link>
      <div className="flex border-t border-gray-100 text-xs">
        <button
          className="flex flex-1 items-center justify-center gap-1 py-2 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          disabled={duplicate.isPending}
          onClick={onDuplicate}
        >
          <Copy className="w-3.5 h-3.5" />{duplicate.isPending ? 'กำลังทำสำเนา…' : 'ทำสำเนา'}
        </button>
        {t.category === 'contract' && !t.is_default && t.is_active && (
          <button
            className="flex flex-1 items-center justify-center gap-1 border-l border-gray-100 py-2 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            disabled={setDefault.isPending}
            onClick={onSetDefault}
          >
            <Star className="w-3.5 h-3.5" />{setDefault.isPending ? 'กำลังตั้ง…' : 'ตั้งเป็นค่าเริ่มต้น'}
          </button>
        )}
      </div>
    </div>
  );
}
